import { useEffect, useState } from "react";
import get from "lodash/get";
import api from "api";
import { latestFilledSubmission } from "modules/dashboards/feedback_form/smart-assist/config/preSalesFemaleCrm";

export const SUPER_FORMULATION_VARIANT_IDS = [
  "67665483661490", "67665483825330", "67665484087474",
  "67665484349618", "67665484578994", "67665484873906",
  "67665485004978", "67665485136050", "67665485299890",
  "67665485627570", "67665485791410", "67665486086322",
  "67665486282930", "67665486479538", "67665486676146",
  "67665487036594", "67665487233202", "67665487429810",
  "67665487626418", "67665487790258", "67665487921330",
  "67665488380082", "67665488543922", "67665488740530",
];

const SUPER_FORMULATION_SET = new Set(SUPER_FORMULATION_VARIANT_IDS);

const sfOnly = (ids) => [...new Set((ids || []).map(String))].filter((id) => SUPER_FORMULATION_SET.has(id));

export const extractRecommendedSuperFormulationIds = (report) => {
  const products = get(report, "product_Ids") || get(report, ["data", "product_Ids"]) || [];
  return sfOnly(
    products.map((entry) =>
      entry !== null && typeof entry === "object"
        ? entry.product_id ?? entry.product_principal_id ?? entry.variant_id
        : entry
    )
  );
};

export const getLastDeliveredOrderSuperFormulationIds = (orders) => {
  const lastDelivered = (Array.isArray(orders) ? orders : [])
    .filter((order) => String(order?.status).toLowerCase() === "delivered")
    .sort((a, b) => new Date(b?.created_at) - new Date(a?.created_at))[0];

  return sfOnly(get(lastDelivered, ["order_meta", "line_items"], []).map((item) => item?.variant_id));
};

export const hasSuperFormulationInOrders = (orders) =>
  (Array.isArray(orders) ? orders : []).some((order) =>
    get(order, ["order_meta", "line_items"], []).some((item) => SUPER_FORMULATION_SET.has(String(item?.variant_id)))
  );

export const resolveVisibleSuperFormulationIds = ({ customerData, recommendedIds }) => {
  if (get(customerData, ["user", "gender"]) !== "F") return [];

  const recommended = sfOnly(recommendedIds);
  return recommended.length ? recommended : getLastDeliveredOrderSuperFormulationIds(customerData?.orders);
};

const hasProducts = (report) => (get(report, "product_Ids") || get(report, ["data", "product_Ids"]) || []).length > 0;

const fetchReport = async (path) => {
  try {
    const response = await api.get(path);
    return response?.data ?? null;
  } catch (error) {
    return null;
  }
};

export const fetchRecommendedSuperFormulationIds = async (customerData) => {
  const syntheticId = latestFilledSubmission(customerData, [])?.syntheticId;
  if (!syntheticId) return [];

  const female = await fetchReport(`dgReport/female?id=${syntheticId}&isWebsite=true`);
  const report = hasProducts(female) ? female : await fetchReport(`dgReport/result?id=${syntheticId}`);
  return extractRecommendedSuperFormulationIds(report);
};

const idsCache = new Map();

const getRecommendedIds = (caseId, customerData) => {
  if (!idsCache.has(caseId)) {
    idsCache.set(caseId, fetchRecommendedSuperFormulationIds(customerData));
  }
  return idsCache.get(caseId);
};

export const useRecommendedSuperFormulationIds = (customerData) => {
  const [recommendedIds, setRecommendedIds] = useState([]);
  const caseId = get(customerData, "id", null);
  const isFemale = get(customerData, ["user", "gender"]) === "F";

  useEffect(() => {
    if (!caseId || !isFemale) {
      setRecommendedIds([]);
      return undefined;
    }
    let cancelled = false;
    getRecommendedIds(caseId, customerData).then((ids) => {
      if (!cancelled) setRecommendedIds(ids);
    });
    return () => {
      cancelled = true;
    };
  }, [caseId, isFemale]);

  return recommendedIds;
};
